import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { MemberFacetCountsDocument, type UserFacet } from "#app/lib/data";

export type MemberFacetCounts = {
  /** Members in the where-set — the "All" row. Unknown until the first
   *  response lands, and no count renders before then (a flash of zeros
   *  would claim an empty view). */
  total: number | undefined;
  /** Members under one key of one facet; 0 for a key the server sent no
   *  bucket for, undefined while the counts are still unknown. */
  count: (facet: UserFacet, key: string) => number | undefined;
};

const UNKNOWN: MemberFacetCounts = { total: undefined, count: () => undefined };

/**
 * The counts a member list's filter chip shows beside its options: the
 * server's `userFacetCounts` over `where` for the facets the chip renders.
 * Apollo dedupes chips that ask for the same where/facets pair, and keeps
 * the last numbers up while a refetch runs.
 */
export function useMemberFacetCounts(
  where: Record<string, unknown> | undefined,
  facets: readonly UserFacet[],
  /** Hold the query — a facet the surface is not offering (the Team facet on
   *  a roster that IS one team) — and read as unknown. */
  { skip = false }: { skip?: boolean } = {},
): MemberFacetCounts {
  const variables = useMemo(() => ({ where, facets: [...facets] }), [where, facets]);
  const { data } = useQuery(MemberFacetCountsDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip,
  });
  return useMemo(() => {
    const result = data?.userFacetCounts;
    if (!result) return UNKNOWN;
    const byKey = new Map<string, number>();
    for (const bucket of result.buckets) {
      if (bucket.key != null) byKey.set(`${bucket.facet}|${bucket.key}`, bucket.count);
    }
    return {
      total: result.total,
      count: (facet, key) => byKey.get(`${facet}|${key}`) ?? 0,
    };
  }, [data]);
}
